import { EDatasourceType } from "@kuika/kuika-cl-model";
import {
  DATASOURCE_CARD_TINTS,
  DatasourceCreateCard,
  findConnector,
  IDatasourceConnector
} from "../datasources-landing-model";

/**
 * The one glyph an editor's top bar opens with, and what its tooltip says it is.
 *
 * Every Datasources and AI Functions editor names its record with the same 16px mask glyph in
 * the bar's switcher chip. The glyph carries its FAMILY in its colour — database blue, API
 * orange, function and AI purple — the same three the landing's create cards wear, so a
 * PostgreSQL connection, its tables and its SQL actions all read as one family, and a brand
 * logo never paints the bar in its own colours (a masked silhouette cannot). What the
 * silhouette alone cannot say — "PostgreSQL", "SQL Action · OrdersDb" — the tooltip does.
 *
 * Pure data, no React: `editor-glyph-icon.tsx` is the only place that turns it into pixels.
 */

/** The landing's three create cards, plus the AI Functions hub. */
export type EditorGlyphFamily = DatasourceCreateCard | "ai";

/**
 * A family's glyph colour. The three card families are the landing's own tints, read from
 * there so the two cannot drift; AI has no create card on that page, so it names its own.
 */
export const EDITOR_GLYPH_COLORS: Record<EditorGlyphFamily, string> = {
  database: DATASOURCE_CARD_TINTS.database.color,
  api: DATASOURCE_CARD_TINTS.api.color,
  function: DATASOURCE_CARD_TINTS.function.color,
  ai: "var(--purple-5)"
};

/** The asset sets a bar glyph is drawn from — a subset of `KuikaSvg`'s `type`. */
export type EditorGlyphIconType = "common" | "datasource-icons";

export interface IEditorGlyph {
  family: EditorGlyphFamily;
  iconName: string;
  iconType: EditorGlyphIconType;
  /** A token, never a hex: dark mode resolves it. */
  color: string;
  /** The glyph's tooltip: what the record is, then what it belongs to. */
  label: string;
}

/**
 * A datasource edited as itself: a connection (SQL, REST, MQTT, Bluetooth…) or a code file
 * (C#, TypeScript, Python…). The TYPE is the subject, not the record — a connection being
 * created has no record yet, and the glyph does not change with the name.
 */
export interface IEditorGlyphDatasourceSubject {
  kind: "datasource";
  datasourceType: EDatasourceType;
}

/**
 * The kinds of action editor. The kind decides the family, not the parent datasource's type:
 * a SQL or Select action is database-blue wherever it lives, a SAP action API-orange.
 */
export type EditorActionKind = "sql" | "select" | "sap" | "blockly";

export interface IEditorGlyphActionSubject {
  kind: "action";
  actionKind: EditorActionKind;
  /** The datasource the action belongs to, as the designer names it (`getDatasourceDisplayName`). */
  datasourceName?: string;
}

/** An endpoint of a REST API collection. */
export interface IEditorGlyphEndpointSubject {
  kind: "endpoint";
  collectionName?: string;
}

export interface IEditorGlyphTableSubject {
  kind: "table";
  /** The datasource the table belongs to, as the designer names it (`getDatasourceDisplayName`). */
  datasourceName?: string;
}

/** The project's one Session Variables screen. */
export interface IEditorGlyphSessionVariablesSubject {
  kind: "sessionVariables";
}

/** The project's one Document Parameters screen. */
export interface IEditorGlyphDocumentParametersSubject {
  kind: "documentParameters";
}

/** An AI Action, AI Agent or Vision Action — `actionType` is the record's own. */
export interface IEditorGlyphAiSubject {
  kind: "ai";
  actionType: EDatasourceType;
  /** What sets this one apart within its kind: an agent's process type ("Hierarchical"). */
  detail?: string;
}

export type EditorGlyphSubject =
  | IEditorGlyphDatasourceSubject
  | IEditorGlyphActionSubject
  | IEditorGlyphEndpointSubject
  | IEditorGlyphTableSubject
  | IEditorGlyphSessionVariablesSubject
  | IEditorGlyphDocumentParametersSubject
  | IEditorGlyphAiSubject;

interface IGlyphEntry {
  family: EditorGlyphFamily;
  iconName: string;
  iconType: EditorGlyphIconType;
  label: string;
}

const DATASOURCE_ICONS: EditorGlyphIconType = "datasource-icons";

/** The C# glyph, as the Sources tree gives the provider files (`data-diagram-scheme-window.tsx`). */
const CSHARP_ICON: string = "hexagon";

/**
 * Types the connector catalog does not describe the way a bar should: the ones it has no entry
 * for (the managed databases, the C# provider files, the AI kinds), and the code runtimes, whose
 * catalog label names the language ("TypeScript") where the bar names what the file is.
 */
const DATASOURCE_GLYPHS: Partial<Record<EDatasourceType, IGlyphEntry>> = {
  [EDatasourceType.ManagedDb]: {
    family: "database",
    iconName: "database",
    iconType: DATASOURCE_ICONS,
    label: "Managed database"
  },
  [EDatasourceType.ManagedDbOracle]: {
    family: "database",
    iconName: "database",
    iconType: DATASOURCE_ICONS,
    label: "Managed database (Oracle)"
  },
  [EDatasourceType.ManagedDbPostgre]: {
    family: "database",
    iconName: "database",
    iconType: DATASOURCE_ICONS,
    label: "Managed database (PostgreSQL)"
  },
  // The connection editor saves an MS SQL Server connection as `CustomDb`.
  [EDatasourceType.CustomDb]: {
    family: "database",
    iconName: "database",
    iconType: DATASOURCE_ICONS,
    label: "MS SQL Server"
  },
  [EDatasourceType.CSharpFunctions]: {
    family: "function",
    iconName: CSHARP_ICON,
    iconType: DATASOURCE_ICONS,
    label: "C# Function"
  },
  [EDatasourceType.SystemCSFunctions]: {
    family: "function",
    iconName: CSHARP_ICON,
    iconType: DATASOURCE_ICONS,
    label: "System C# Function"
  },
  [EDatasourceType.SystemJSFunctions]: {
    family: "function",
    iconName: "typescript",
    iconType: DATASOURCE_ICONS,
    label: "System JavaScript Function"
  },
  [EDatasourceType.CustomAuthenticationProvider]: {
    family: "function",
    iconName: CSHARP_ICON,
    iconType: DATASOURCE_ICONS,
    label: "C# Authentication Provider"
  },
  [EDatasourceType.CustomAuthorizationProvider]: {
    family: "function",
    iconName: CSHARP_ICON,
    iconType: DATASOURCE_ICONS,
    label: "C# Authorization Provider"
  },
  [EDatasourceType.CustomVerificationCodeSenderProvider]: {
    family: "function",
    iconName: CSHARP_ICON,
    iconType: DATASOURCE_ICONS,
    label: "C# Verification Code Sender"
  },
  [EDatasourceType.CustomConnectionStringProvider]: {
    family: "function",
    iconName: CSHARP_ICON,
    iconType: DATASOURCE_ICONS,
    label: "C# Connection String Provider"
  },
  [EDatasourceType.CustomExternalTokenValidatorProvider]: {
    family: "function",
    iconName: CSHARP_ICON,
    iconType: DATASOURCE_ICONS,
    label: "C# External Token Validator"
  },
  [EDatasourceType.CustomMailProvider]: {
    family: "function",
    iconName: CSHARP_ICON,
    iconType: DATASOURCE_ICONS,
    label: "C# Mail Provider"
  },
  [EDatasourceType.JavaScriptAction]: {
    family: "function",
    iconName: "typescript",
    iconType: DATASOURCE_ICONS,
    label: "TypeScript Function"
  },
  [EDatasourceType.PythonAction]: {
    family: "function",
    iconName: "python",
    iconType: DATASOURCE_ICONS,
    label: "Python Function"
  },
  [EDatasourceType.JavaAction]: {
    family: "function",
    iconName: "java",
    iconType: DATASOURCE_ICONS,
    label: "Java Function"
  },
  [EDatasourceType.GoAction]: {
    family: "function",
    iconName: "go",
    iconType: DATASOURCE_ICONS,
    label: "Go Function"
  },
  [EDatasourceType.RustAction]: {
    family: "function",
    iconName: "rust",
    iconType: DATASOURCE_ICONS,
    label: "Rust Function"
  },
  [EDatasourceType.AIActions]: { family: "ai", iconName: "ai-action", iconType: DATASOURCE_ICONS, label: "AI Action" },
  [EDatasourceType.AIAgent]: { family: "ai", iconName: "ai-agent", iconType: DATASOURCE_ICONS, label: "AI Agent" },
  [EDatasourceType.VisionAction]: {
    family: "ai",
    iconName: "vision-action",
    iconType: DATASOURCE_ICONS,
    label: "Vision Action"
  }
};

/** A type nothing above describes: the plain database glyph, as `getConnectionGroup` falls back. */
const FALLBACK_DATASOURCE_GLYPH: IGlyphEntry = {
  family: "database",
  iconName: "database",
  iconType: DATASOURCE_ICONS,
  label: "Datasource"
};

const ACTION_GLYPHS: Record<EditorActionKind, IGlyphEntry> = {
  // The Sources tree's action glyph.
  sql: { family: "database", iconName: "lightning", iconType: "common", label: "SQL Action" },
  select: { family: "database", iconName: "lightning", iconType: "common", label: "Select Action" },
  sap: { family: "api", iconName: "sap", iconType: DATASOURCE_ICONS, label: "SAP Action" },
  blockly: { family: "function", iconName: "puzzle", iconType: DATASOURCE_ICONS, label: "Blockly Action" }
};

const ENDPOINT_GLYPH: IGlyphEntry = {
  family: "api",
  iconName: "cloud-cog",
  iconType: DATASOURCE_ICONS,
  label: "REST API Endpoint"
};

const TABLE_GLYPH: IGlyphEntry = { family: "database", iconName: "table", iconType: "common", label: "Table" };

/** The key glyph the Sources tree and the landing give the same row. */
const SESSION_VARIABLES_GLYPH: IGlyphEntry = {
  family: "database",
  iconName: "key",
  iconType: "common",
  label: "Session Variables"
};

/**
 * The file glyph the Sources tree gives the same node. Database family: a parameter's value is a field
 * of a database datasource's action, which is where the tool lives in the tree.
 */
const DOCUMENT_PARAMETERS_GLYPH: IGlyphEntry = {
  family: "database",
  iconName: "file-text",
  iconType: "common",
  label: "Document Parameters"
};

/** An AI record of a kind this module does not know — the hub's own word for all three. */
const FALLBACK_AI_GLYPH: IGlyphEntry = {
  family: "ai",
  iconName: "ai-action",
  iconType: DATASOURCE_ICONS,
  label: "AI Function"
};

const getDatasourceGlyphEntry = (type: EDatasourceType): IGlyphEntry => {
  const own: IGlyphEntry | undefined = DATASOURCE_GLYPHS[type];
  if (own) return own;
  const connector: IDatasourceConnector | undefined = findConnector(type);
  if (connector) {
    return { family: connector.card, iconName: connector.iconName, iconType: DATASOURCE_ICONS, label: connector.label };
  }
  return FALLBACK_DATASOURCE_GLYPH;
};

/**
 * Which family a datasource type belongs to. Total over `EDatasourceType`: a connector takes its
 * create card, the rest are named above, and anything else is a database — `getConnectionGroup`'s
 * own fallback.
 */
export const getDatasourceGlyphFamily = (type: EDatasourceType): EditorGlyphFamily =>
  getDatasourceGlyphEntry(type).family;

/** "SQL Action" + "OrdersDb" → "SQL Action · OrdersDb"; a missing detail leaves the kind alone. */
const withDetail = (label: string, detail: string | undefined): string => {
  const trimmed: string = (detail ?? "").trim();
  return trimmed ? `${label} · ${trimmed}` : label;
};

const toGlyph = (entry: IGlyphEntry, detail?: string): IEditorGlyph => ({
  family: entry.family,
  iconName: entry.iconName,
  iconType: entry.iconType,
  color: EDITOR_GLYPH_COLORS[entry.family],
  label: withDetail(entry.label, detail)
});

const assertNever = (subject: never): never => {
  throw new Error(`Unhandled editor glyph subject: ${JSON.stringify(subject)}`);
};

export const getEditorGlyph = (subject: EditorGlyphSubject): IEditorGlyph => {
  switch (subject.kind) {
    case "datasource":
      return toGlyph(getDatasourceGlyphEntry(subject.datasourceType));
    case "action":
      return toGlyph(ACTION_GLYPHS[subject.actionKind], subject.datasourceName);
    case "endpoint":
      return toGlyph(ENDPOINT_GLYPH, subject.collectionName);
    case "table":
      return toGlyph(TABLE_GLYPH, subject.datasourceName);
    case "sessionVariables":
      return toGlyph(SESSION_VARIABLES_GLYPH);
    case "documentParameters":
      return toGlyph(DOCUMENT_PARAMETERS_GLYPH);
    case "ai": {
      const entry: IGlyphEntry | undefined = DATASOURCE_GLYPHS[subject.actionType];
      return toGlyph(entry?.family === "ai" ? entry : FALLBACK_AI_GLYPH, subject.detail);
    }
    default:
      return assertNever(subject);
  }
};
